import { useEffect, useMemo, useState } from 'react';
import { ValidationResult } from 'joi';

import Loading from '../../../shared/Loading';
import ModalV2 from '../../../shared/ModalV2';
import { PackExportResultsModal } from '../PackExportResulsModal/PackExportResultsModal.tsx';
import { PackExportSelectModal } from '../PackExportSelectModal/PackExportSelectModal.tsx';
import { Modal } from '../../../shared';

import {
    IapExportResult,
    IapExportStatus,
    useExportToXsolla,
    useIapExportToApple,
    useIapExportToGoogle,
} from '../../../hooks/packs';

import { handleError } from '../../../utils/utils';

import { ApiAvailableStore, Store } from '../../../types/enums/store.enum';

import { Pack } from '../../../types/packTypes';

import styles from './PackExport.module.scss';

const StoreToName: Record<ApiAvailableStore, string> = {
    [Store.APPLE]: 'Apple Store Connect',
    [Store.GOOGLE]: 'Google Play Store',
};

enum ReadinessStatus {
    Ready = 'Ready',
    MissingPrices = 'MissingPrices',
    MissingEnglish = 'MissingEnglish',
    ValidationErrors = 'ValidationErrors',
    Unsaved = 'Unsaved',
    UnprocessedMedias = 'UnprocessedMedias',
}

type PackExportReadinessArgs = {
    validationResults: {
        errors: ValidationResult;
        warnings: ValidationResult;
    };
    hasUnsavedChanges: boolean;
    pack: Pack;
    // True when any media in the pack is still processing or failed to
    // process on the server. Media are stored as ids in the pack body, so
    // this is computed by the caller from the client-side media state.
    hasUnprocessedMedias?: boolean;
};

export const getPackExportReadinessStatus = ({
    validationResults,
    hasUnsavedChanges,
    pack,
    hasUnprocessedMedias = false,
}: PackExportReadinessArgs) => {
    if (hasUnsavedChanges) {
        return ReadinessStatus.Unsaved;
    }

    if (validationResults.errors.error) {
        return ReadinessStatus.ValidationErrors;
    }

    if (hasUnprocessedMedias) {
        return ReadinessStatus.UnprocessedMedias;
    }

    if (
        validationResults.warnings?.error?.details?.find((warn) =>
            warn.path.includes('en'),
        )
    ) {
        return ReadinessStatus.MissingEnglish;
    }

    if (pack.priceLevels.some((priceLevel) => !priceLevel.price)) {
        return ReadinessStatus.MissingPrices;
    }

    return ReadinessStatus.Ready;
};

enum ExportStep {
    NotStarted,
    Selection,
    Exporting,
    Results,
}

type PackExportProps = {
    pack: Pack;
    checkExportReadiness: () => ReadinessStatus;
};

export const PackExport = (props: PackExportProps) => {
    const { pack, checkExportReadiness } = props;

    const [store, setStore] = useState<ApiAvailableStore | null>(null);
    const [exportStep, setExportStep] = useState<ExportStep>(
        ExportStep.NotStarted,
    );
    const [accumulatedExportResults, setAccumulatedExportResults] = useState<
        IapExportResult[]
    >([]);

    const productIds = useMemo(
        () => pack.priceLevels.map((pl) => pl.productId),
        [pack],
    );

    const xsollaSkus = pack.priceLevels
        .flatMap((pl) => pl.platformData)
        .filter((pd) => pd.store === Store.XSOLLA);

    const {
        status: exportIapToAppleStatus,
        exportIap: exportIapToApple,
        results: exportIapToAppleResults,
        error: exportIapToAppleError,
    } = useIapExportToApple();

    const {
        status: exportIapToGoogleStatus,
        exportIap: exportIapToGoogle,
        results: exportIapToGoogleResults,
        error: exportIapToGoogleError,
    } = useIapExportToGoogle();

    const { mutate: exportIapToXsolla, isPending: isExportIapToXsollaPending } =
        useExportToXsolla();

    useEffect(() => {
        if (exportIapToAppleStatus === IapExportStatus.Exported) {
            setExportStep(ExportStep.Results);
        } else if (exportIapToAppleStatus === IapExportStatus.Failed) {
            handleError(
                'Failed to send data to the store',
                exportIapToAppleError,
            );
        }
    }, [exportIapToAppleStatus, exportIapToAppleError]);

    useEffect(() => {
        if (exportIapToGoogleStatus === IapExportStatus.Exported) {
            setExportStep(ExportStep.Results);
        } else if (exportIapToGoogleStatus === IapExportStatus.Failed) {
            handleError(
                'Failed to send data to the store',
                exportIapToGoogleError,
            );
        }
    }, [exportIapToGoogleStatus, exportIapToGoogleError]);

    useEffect(() => {
        if (store) {
            showExportInitModal(store);
        }
    }, [store]);

    useEffect(() => {
        if (accumulatedExportResults.length > 0) {
            setExportStep(ExportStep.Selection);
        }
    }, [accumulatedExportResults]);

    const exportIap = (store: ApiAvailableStore, ids?: string[]) => {
        setExportStep(ExportStep.Exporting);

        switch (store) {
            case ApiAvailableStore.APPLE:
                return exportIapToApple(pack.packId, ids);
            case ApiAvailableStore.GOOGLE:
                return exportIapToGoogle(pack.packId, ids);
        }
    };

    const getOnRetryClickHandler = (store: ApiAvailableStore) => () => {
        const { results } = getExportDataByStore(store);

        setAccumulatedExportResults((prevState) => {
            const prevResultsMap = new Map(
                prevState.map((r) => [r.productId, r]),
            );

            return results.map((r) => {
                const prevResult = prevResultsMap.get(r.productId);

                if (prevResult && r.skipped) {
                    return prevResult;
                }

                return r;
            });
        });
    };

    async function showExportInitModal(store: ApiAvailableStore) {
        const status = checkExportReadiness();

        if (status === ReadinessStatus.Unsaved) {
            await Modal.alert({
                heading: '⚠️ Unsaved changes',
                text: 'You must save all changes before exporting to the store fronts!',
            });
            return;
        }

        if (status === ReadinessStatus.MissingPrices) {
            await Modal.alert({
                heading: '❌ Missing prices',
                text: 'Not all SKUs have defined prices!',
            });
            return;
        }

        if (status === ReadinessStatus.ValidationErrors) {
            await Modal.alert({
                heading: '⚠️ Errors in pack',
                text: 'Fix all errors in the pack before exporting to the store fronts!',
            });
            return;
        }

        if (status === ReadinessStatus.MissingEnglish) {
            await Modal.alert({
                heading: '⚠️ Errors in English data',
                text: 'Please fix English display name and description before exporting to the store fronts!',
            });
            return;
        }

        if (status === ReadinessStatus.UnprocessedMedias) {
            await Modal.alert({
                heading: '⚠️ Images and videos not ready',
                text: 'All images and videos must finish processing — and any failed uploads must be removed — before exporting to the store fronts!',
            });
            return;
        }

        const storeName = StoreToName[store];
        const isConfirmed = await Modal.confirm({
            heading: `Export to ${storeName}`,
            text: `This will export all data from this pack to production ${storeName} storefront as In-App Purchases. Do you want to continue?`,
        });

        if (!isConfirmed) {
            setStore(null);
            return;
        }

        setExportStep(ExportStep.Selection);
    }

    const onExportInit = (store: ApiAvailableStore) => () => {
        setStore(store);
    };

    const onExportToXsolla = async () => {
        const status = checkExportReadiness();

        if (status === ReadinessStatus.Unsaved) {
            await Modal.alert({
                heading: '⚠️ Unsaved changes',
                text: 'You must save all changes before exporting to Xsolla!',
            });
            return;
        }

        const isConfirmed = await Modal.confirm({
            heading: 'Export to Xsolla',
            text: 'This will create or update the Xsolla virtual item for this pack. Do you want to continue?',
        });

        if (!isConfirmed) {
            return;
        }

        exportIapToXsolla(pack.packId, {
            onSuccess: () => {
                Modal.alert({
                    heading: '✅ Exported to Xsolla',
                    text: 'The pack was successfully exported to Xsolla.',
                });
            },
            onError: (error) => {
                handleError('Failed to send data to Xsolla', error);
            },
        });
    };

    const onExportStart = (ids: string[]) => {
        if (!store) {
            return;
        }

        exportIap(store, ids);
    };

    const onClose = () => {
        setStore(null);
        setAccumulatedExportResults([]);
        setExportStep(ExportStep.NotStarted);
    };

    const getExportDataByStore = (store: ApiAvailableStore) => {
        switch (store) {
            case ApiAvailableStore.APPLE:
                return {
                    status: exportIapToAppleStatus,
                    results: exportIapToAppleResults,
                    error: exportIapToAppleError,
                };
            case ApiAvailableStore.GOOGLE:
                return {
                    status: exportIapToGoogleStatus,
                    results: exportIapToGoogleResults,
                    error: exportIapToGoogleError,
                };
        }
    };

    if (!store) {
        const isXsollaExporting = isExportIapToXsollaPending;

        return (
            <div className={styles.packExport}>
                <button
                    onClick={onExportInit(ApiAvailableStore.APPLE)}
                    disabled={
                        exportIapToGoogleStatus === IapExportStatus.Exporting
                    }
                >
                    Export to Apple Store
                </button>

                <button
                    onClick={onExportInit(ApiAvailableStore.GOOGLE)}
                    disabled={
                        exportIapToAppleStatus === IapExportStatus.Exporting
                    }
                >
                    Export to Google Play Store
                </button>

                {xsollaSkus.length === 1 && (
                    <button
                        onClick={onExportToXsolla}
                        disabled={isXsollaExporting}
                    >
                        Export to Xsolla
                    </button>
                )}

                {isXsollaExporting && (
                    <ModalV2>
                        <Loading
                            style={{ margin: '50px 0', textAlign: 'center' }}
                            text="Exporting to Xsolla..."
                        />
                    </ModalV2>
                )}
            </div>
        );
    }

    if (exportStep === ExportStep.Selection) {
        return (
            <PackExportSelectModal
                productIds={productIds}
                onExport={onExportStart}
                onClose={onClose}
                exportResults={accumulatedExportResults}
            />
        );
    }

    if (exportStep === ExportStep.Exporting) {
        return (
            <ModalV2>
                <Loading
                    style={{ margin: '50px 0', textAlign: 'center' }}
                    text="Exporting..."
                />
            </ModalV2>
        );
    }

    if (exportStep === ExportStep.Results) {
        return (
            <PackExportResultsModal
                store={store}
                exportResults={getExportDataByStore(store).results}
                onClose={onClose}
                onRetry={getOnRetryClickHandler(store)}
            />
        );
    }

    return null;
};
